"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function VipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card/90 backdrop-blur-md border border-border rounded-sm text-center shadow-2xl">
                <h3 className="text-2xl font-default font-bold text-foreground mb-4">Inquiry Received</h3>
                <p className="text-muted-foreground mb-6">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="w-full border-primary text-primary hover:bg-primary/10">
                    Submit Another Inquiry
                </Button>
            </div>;
  }
  const inputClass = "bg-background/50 border-border/50 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-primary";
  const labelClass = "text-foreground font-semibold uppercase tracking-wider text-xs";
  return <Form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem className="h-fit">
                            <FormLabel className={labelClass}>Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Your Full Name" className={inputClass} {...field} />
                            </FormControl>
                            <FormDescription className="text-muted-foreground/70 text-xs">Enter your legal name for verification</FormDescription>
                            <FormMessage className="text-destructive font-medium" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="h-fit">
                            <FormLabel className={labelClass}>Corporate Email</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="your.email@company.com" className={inputClass} {...field} />
                            </FormControl>
                            <FormMessage className="text-destructive font-medium" />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem className="h-fit">
                            <FormLabel className={labelClass}>Direct Line</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+1 (800) 555-0123" className={inputClass} {...field} />
                            </FormControl>
                            <FormMessage className="text-destructive font-medium" />
                        </FormItem>} />

                <FormField control={control} name="revenue" rules={{
        required: "Annual revenue is required"
      }} render={({
        field
      }) => <FormItem className="h-fit">
                            <FormLabel className={labelClass}>Annual Revenue</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                    <SelectTrigger className={inputClass}>
                                        <SelectValue placeholder="Select revenue bracket" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="Less than $5M">Less than $5M</SelectItem>
                                    <SelectItem value="$5M - $10M">$5M - $10M</SelectItem>
                                    <SelectItem value="$10M - $25M">$10M - $25M</SelectItem>
                                    <SelectItem value="$25M - $50M">$25M - $50M</SelectItem>
                                    <SelectItem value="$50M+">$50M+</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormDescription className="text-muted-foreground/70 text-xs">Select your current revenue bracket</FormDescription>
                            <FormMessage className="text-destructive font-medium" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
                        <FormLabel className={labelClass}>Consultation Window</FormLabel>
                        <FormControl>
                            <Input type="date" className={inputClass} {...field} />
                        </FormControl>
                        <FormDescription className="text-muted-foreground/70 text-xs">Select a preferred date for a confidential briefing</FormDescription>
                        <FormMessage className="text-destructive font-medium" />
                    </FormItem>} />

            <FormField control={control} name="message" rules={{
      maxLength: {
        value: 500,
        message: "Maximum 500 characters"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className={labelClass}>Strategic Objectives</FormLabel>
                        <FormControl>
                            <textarea placeholder="Briefly describe your exit or investment goals..." className={`flex min-h-[100px] w-full rounded-sm border px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${inputClass}`} {...field} />
                        </FormControl>
                        <FormMessage className="text-destructive font-medium" />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
                    <p className="text-destructive font-medium">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-gradient-to-r from-primary to-secondary text-primary-foreground hover:opacity-90 text-lg font-default uppercase tracking-widest mt-6 shadow-lg">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Securing...
                    </> : 'Secure Your Legacy'}
            </Button>
        </Form>;
}
export default function VipInsiderAccess() {
  return <section data-section-id="224880" id="secure-your-legacy" className="dark bg-background text-foreground relative overflow-hidden py-16 md:py-20 border-t border-primary/20">
            <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657590/img/boardroom_coastal_view.png)] bg-cover bg-center opacity-20"></div>
            <div className="relative z-10 container mx-auto px-6">
                <div className="max-w-2xl mx-auto text-center bg-background/80 backdrop-blur-sm border border-border/30 rounded-lg px-6 py-10 md:px-12 md:py-12">
                    <h3 className="text-3xl font-default font-bold text-foreground mb-2">Confidential Advisory Access</h3>
                    <p className="text-muted-foreground font-default">Request a private consultation with our M&A specialists.</p>
                    <div className="flex justify-center mt-8">
                        <Button asChild size="lg" className="px-10 h-14 text-base font-mono uppercase tracking-widest">
                            <a href="/contact/">Schedule a Confidential Consultation</a>
                        </Button>
                    </div>
                </div>
            </div>
        </section>;
}